import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';

import { getArticleIcon } from '../_helpers';
import { searchArticles, getArticleData } from '../_services';
import { Button, Icon, Checkbox } from './';

//Define basic search/filter options
const defaultOptions = {
  typesFilter: [],
  excludeStubs: false,
  excludeWIP: false,
  excludeDrafts: false,
  excludePrivate: false,
};

export function ArticlePicker({ value, onChange }) {
  //Search Value
  const [searchVal, setSearchVal] = useState('');
  //All results from API
  const [results, setResults] = useState([]);
  //Search Filter Options
  const [options, setOptions] = useState(defaultOptions);
  //Selected article (if any)
  const [chosenArticle, setChosenArticle] = useState(undefined);
  //Show the Options popup
  const [showOptions, setShowOptions] = useState(false);

  //Get World data for API calls
  const { details: world } = useSelector((state) => state.world);

  //Ref to track input state (size, position, etc.)
  const inputRef = useRef();

  //Ref to track Options popup (to close on external click, etc)
  const optionsRef = useRef();

  //Clear the search input (and results, as a side effect)
  const handleClearSearch = () => {
    setSearchVal('');
  };

  //Update search value state (and results, as a side effect)
  const handleSearchChange = (ev) => {
    const { value } = ev.target;
    setSearchVal(value);
  };

  //Update the Options state
  const handleOptionsChange = (newOptions) => {
    // console.log('New Options:', { ...options, ...newOptions });
    setOptions({ ...options, ...newOptions });
  };

  //Toggle the display of the Options popup
  const handleOptionsDisplayToggle = (ev) => {
    setShowOptions(!showOptions);
  };

  //Update the selected article
  const handleArticleSelect = (article) => {
    console.log('Article', article);
    setChosenArticle(article);
    handleClearSearch();
    if (onChange) {
      onChange(article.id);
    }
  };

  //Update the results as the search value changes
  useEffect(() => {
    if (searchVal && searchVal.length >= 3) {
      let searching = true;
      searchArticles(searchVal, world.waID, options).then((searchResults) => {
        if (searching) {
          // console.log('Results: ', searchResults);
          setResults(searchResults);
        }
      });
      return () => (searching = false);
    } else {
      setResults([]);
    }
  }, [searchVal]);

  //Update the selected article based on value
  useEffect(() => {
    if (value && value.length > 0) {
      // console.log('Defualt Article ID value', value);
      let loading = true;
      getArticleData(value).then((result) => {
        if (loading) {
          console.log('Found Article! Result is', result);
          setChosenArticle(result.data);
        }
      });
      return () => (loading = false);
    }
  }, []);

  //Effect to close the Options popup
  useEffect(() => {
    const handleOutsideClick = (ev) => {
      if (optionsRef.current && !optionsRef.current.contains(ev.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      window.addEventListener('click', handleOutsideClick);
    }

    return () => {
      window.removeEventListener('click', handleOutsideClick);
    };
  }, [showOptions]);

  const filteredResults =
    results &&
    results
      .filter((result) => {
        if (options.excludeDrafts && result.is_draft) {
          return false;
        }
        if (options.excludePrivate && result.state !== 'public') {
          return false;
        }
        if (options.excludeWIP && result.is_wip) {
          return false;
        }
        if (options.excludeStubs) {
          if (result.wordcount && Number.isInteger(result.wordcount)) {
            if (result.wordcount >= 300) {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        }
        return true;
      })
      .map((article) => (
        <ResultItem
          active={chosenArticle && chosenArticle.id === article.id}
          article={article}
          key={article.id}
          select={handleArticleSelect}
        />
      ));

  //Flag to display results list
  const showResults =
    searchVal && searchVal.length >= 3 && results && results.length > 0;

  //Flag to display 'No Results'
  const showEmpty =
    searchVal &&
    searchVal.length >= 3 &&
    (!results || results.length === 0 || filteredResults.length === 0);

  //Set width of results list to equal input (using ref)
  const resultsWidth =
    inputRef && inputRef.current ? inputRef.current.clientWidth + 1 : '100%';

  //Set a default placeholder for the input bar
  // Shows the selected article's title if present
  const defaultPlaceholder =
    chosenArticle && chosenArticle.title
      ? chosenArticle.title
      : 'Search Articles';

  return (
    <div className="chronos-article-picker-component">
      <ChosenArticle
        article={chosenArticle}
        selected={value && chosenArticle && value === chosenArticle.id}
      />
      <div className="article-picker-search">
        <div className="article-search-input input-group">
          <input
            type="text"
            value={searchVal}
            onChange={handleSearchChange}
            className="form-control"
            placeholder={defaultPlaceholder}
            ref={inputRef}
          />
          {searchVal && searchVal.length > 0 && (
            <Button onClick={handleClearSearch} className="form-control-clear">
              <Icon icon="far fa-times fa-fw" />
            </Button>
          )}

          <div className="input-group-append">
            <Button
              onClick={handleOptionsDisplayToggle}
              data-tip="Show Search Options"
              data-for="articlePickerTooltip"
              className="btn btn-secondary"
            >
              <Icon icon="far fa-cog" />
            </Button>
          </div>
        </div>
        {showOptions && (
          <SearchOptions
            options={options}
            update={handleOptionsChange}
            innerRef={optionsRef}
          />
        )}
      </div>
      <div className="article-picker-results" style={{ width: resultsWidth }}>
        <ul className="article-picker-result-list">
          {showResults && filteredResults}
          {showEmpty && <EmptyResult />}
        </ul>
      </div>
      <ReactTooltip effect="solid" id="articlePickerTooltip" />
    </div>
  );
}

function ChosenArticle({ article, selected }) {
  const showEmptyArticle = !article;

  return (
    <div className="chronos-article-picker-display">
      {showEmptyArticle && (
        <div className="article-picker-display-wrapper">
          <div className="article-picker-display-icon">
            <Icon icon="far fa-file-minus fa-2x" />
          </div>
          <div className="article-picker-display-info">
            <h4 className="article-picker-display-title">
              No Article Selected
            </h4>
          </div>
        </div>
      )}
      {!showEmptyArticle && (
        <div className="article-picker-display-wrapper">
          <div className="article-picker-display-icon">
            {selected && <Icon icon="fal fa-file-check fa-2x" />}
            {!selected && <Icon icon="fal fa-file-plus fa-2x" />}
          </div>
          <div className="article-picker-display-info">
            <h4 className="article-picker-display-title">{article.title}</h4>
            <p className="article-picker-display-template">
              {article.template}
            </p>
            <p className="article-picker-display-url">{article.url}</p>
            <p className="article-picker-display-stats">
              <span className="article-stat article-wip">
                {article.is_wip ? 'WIP' : 'Done'}
              </span>
              <span className="article-stat article-draft">
                {article.is_draft ? 'Draft' : 'Published'}
              </span>
              <span className="article-stat article-privacy">
                {article.state}
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function SearchOptions({ options, update, innerRef }) {
  const handleBooleanOption = (field, value) => {
    update({ [field]: value });
  };
  return (
    <div className="article-picker-search-options" ref={innerRef}>
      <div className="chronos-menu-heading">Search Options</div>
      <div className="article-search-option">
        <label htmlFor="excludeStubs">
          <span className="article-search-option-label">Exclude Stubs?</span>
          <Checkbox
            value={options.excludeStubs}
            onChange={(value) => handleBooleanOption('excludeStubs', value)}
            custom
          />
        </label>
      </div>
      <div className="article-search-option">
        <label htmlFor="excludeWIP">
          <span className="article-search-option-label">Exclude WIP?</span>
          <Checkbox
            value={options.excludeWIP}
            onChange={(value) => handleBooleanOption('excludeWIP', value)}
            custom
          />
        </label>
      </div>
      <div className="article-search-option">
        <label htmlFor="excludeDrafts">
          <span className="article-search-option-label">Exclude Drafts?</span>
          <Checkbox
            value={options.excludeDrafts}
            onChange={(value) => handleBooleanOption('excludeDrafts', value)}
            custom
          />
        </label>
      </div>
      <div className="article-search-option">
        <label htmlFor="excludePrivate">
          <span className="article-search-option-label">Exclude Private?</span>
          <Checkbox
            value={options.excludePrivate}
            onChange={(value) => handleBooleanOption('excludePrivate', value)}
            custom
          />
        </label>
      </div>
    </div>
  );
}

function ResultItem({ active, article, select }) {
  const handleArticleSelect = () => {
    if (select) {
      select(article);
    }
  };
  const { icon } = getArticleIcon(article);
  let classes = ['article-picker-result-item'];
  if (active) classes.push('active-article');
  return (
    <li className={classes.join(' ')} onClick={handleArticleSelect}>
      {icon && icon.length > 0 && <Icon icon={icon} />}
      &nbsp;{article.title}
    </li>
  );
}

function EmptyResult() {
  return (
    <li className="article-picker-result-item no-results">No Results Found</li>
  );
}
